import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  find,
  get,
  groupBy,
  sortBy,
  maxBy,
  cloneDeep,
  mapValues,
  values,
  isUndefined,
  isEmpty,
  isEqual,
  size,
} from 'lodash';
import {
  API,
  GlobalActions,
  ToastActions,
  NavigationHelper,
  LocationHelper,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelFooter,
  SidePanelContent,
  SidePanelSection,
  ApproverList,
  Assignments,
  Tracking,
  MessageBlockCard,
  PlayControl,
  VibeModal,
  VibeTooltip,
  VibeButton,
  VibeIcon,
  viChat,
  viCopy,
  viTimeHistory,
  viClose,
  viChangeRequest,
  viCheckCircle,
  viMusicVoice,
  viDownload,
  viUpload,
  viArchive,
  viUnarchive,
  viRedo,
  viEmail,
  viCloseCircle,
  viPlayCircleOutline,
  withRouter,
  color,
} from 'vibeguide';
import MediaType from './Section/MediaType';
import MediaItems from './Section/MediaItems';
import Information from './Section/Information';
import Upload from './Section/Upload';
import Script from './Section/Script';
import PublishingOptions from './Section/PublishingOptions';
import ApproverOptions from './Section/Approvers/ApproverOptions';
import RequestChanges from './Actions/RequestChanges';
import AssignVoiceTalent from './Actions/AssignVoiceTalent';
import AssignApprover from './Actions/AssignApprover';
import DownloadCut from './Actions/DownloadCut';
import UploadCut from './Actions/UploadCut';
import MessageLocations from './MessageLocations';
import MessageCompanies from './MessageCompanies';
import MessageBlocks from './MessageBlocks';
import './MessageDetails.scss';
// import Attachments from './Section/Attachments';

class MessageDetails extends PureComponent {
  constructor(props) {
    super(props);

    // Store which values are updated that require an endpoint
    this.updated = {};
    this.origMessage = {};
    this.origMessageSaved = false;

    this.state = {
      requestChanges: false,
      assignVoiceTalent: false,
      assignApprover: false,
      downloadCut: false,
      uploadCut: false,
      confirmArchive: false,
      confirmApprove: false,
      confirmApproveOverride: false,
      localAttachments: [],
      showEmailApprover: false,
      showConfirmNoMB: false,
      showInvalidMessageBlocks: false,
      invalidMessageBlocks: [],
      // requireAdApproval: false,
      partnerId: '',
      partnerName: '',
      newVisualFiles: [],
      savedAdditionalVisualFiles: false,
      resolutionErrors: false,
    };
  }

  componentDidMount() {
    const {
      user,
      isNew,
      onUpdate,
    } = this.props;

    const canSetCompany = user.sysAdmin;

    if (!canSetCompany && isNew) {
      onUpdate({
        companyId: user.companyId,
        companyName: user.companyName,
      });
    }

    if (user.accountType === 'partner') {
      onUpdate({
        partnerId: user.partnerId,
        partnerName: user.partnerName,
      });
    }
  }

  componentDidUpdate() {
    const {
      type,
      message,
    } = this.props;

    if (!this.origMessageSaved && message._id) {
      this.origMessageSaved = true;
      this.origMessage = message;
    }

    if (this.origMessage._id !== message._id) {
      // The message was changed, user clicked another request without closing the sidebar
      this.origMessage = message;
    }

    const {
      origMessage,
    } = this;

    if (this.origMessageSaved) {
      const messageBlockIds = type === 'library'
        ? get(message, 'messageBlocks', []).map(messageBlock => messageBlock._id)
        : get(message, 'messageLists', []).map(messageBlock => messageBlock._id);
      const origMessageBlockIds = type === 'library'
        ? get(origMessage, 'messageBlocks', []).map(messageBlock => messageBlock._id)
        : get(origMessage, 'messageLists', []).map(messageBlock => messageBlock._id);

      // Only track what fields are updated once the original message is saved
      if (message.name !== origMessage.name) {
        this.updated.name = true;
      }
      if (!this.updated.publishMode
        && (message.publishMode !== origMessage.publishMode
          || (!isEqual(message.partnerApproval, origMessage.partnerApproval))
          || (!isEqual(message.clientApproval, origMessage.clientApproval)))
      ) {
        this.updated.publishMode = true;
      }
      if (message.script !== origMessage.script) {
        this.updated.script = true;
      }
      if (message.instructions !== origMessage.instructions) {
        this.updated.instructions = true;
      }
      if (
        message.startDate !== origMessage.startDate
        || message.endDate !== origMessage.endDate
        || message.dueDate !== origMessage.dueDate
      ) {
        // We can't check if there was a previous start/end date in case they add one when originally it had none
        this.updated.dates = true;
      }
      if (JSON.stringify(message.daysOfWeek) !== JSON.stringify(origMessage.daysOfWeek)) {
        this.updated.daysOfWeek = true;
      }
      if (JSON.stringify(message.tags) !== JSON.stringify(origMessage.tags)) {
        // We can't check if there were previous tags in case they add one when originally it had none
        this.updated.tags = true;
      }
      if (JSON.stringify(messageBlockIds) !== JSON.stringify(origMessageBlockIds)) {
        this.updated.messageLists = true;
      }
      if (!isEqual(message.locations, origMessage.locations)) {
        this.updated.locations = true;
      }
      if (message.trackingUrls.length > 0) {
        message.trackingUrls = message.trackingUrls.filter((e) => { return e; });
        if (!isEqual(message.trackingUrls, origMessage.trackingUrls)) {
          this.updated.trackingUrls = true;
        } else {
          this.updated.trackingUrls = false;
        }
      } else {
        this.updated.trackingUrls = false;
      }
      if (message.category !== origMessage.category) {
        this.updated.category = true;
      }
    }
  }

  onUpdateVisualFiles = (file, index, actualDimensions, durationSeconds, targetScreen, isError, isNew, onProgress) => {
    const {
      newVisualFiles,
    } = this.state;

    const updatedTargetScreen = {
      orientation: targetScreen.orientation,
      resolution: [targetScreen.size[0], targetScreen.size[1]],
    };

    const updatedVisualFiles = newVisualFiles.map(visFile => visFile);
    const uploadFile = {
      file,
      targetScreen: updatedTargetScreen,
      durationSeconds,
      isError,
      isNew,
      onProgress,
      actualDimensions,
    };

    // if replacing a file, try to match the target screen of the
    // incoming file with the one in the newVisualFiles array.

    let matchingIndex = -1;
    newVisualFiles.forEach((visFile, i) => {
      if ((visFile.targetScreen.resolution[0] === targetScreen.size[0])
        && (visFile.targetScreen.resolution[1] === targetScreen.size[1])
        && visFile.targetScreen.orientation === targetScreen.orientation) {
        matchingIndex = i;
      }
    });

    if (matchingIndex !== -1) {
      updatedVisualFiles.splice(matchingIndex, 1, uploadFile);
    } else {
      updatedVisualFiles.push(uploadFile);
    }

    this.setState({
      newVisualFiles: updatedVisualFiles,
    });
  };

  /**
   * Get Message Approver Type
   */
  getApproverType = () => {
    const {
      message,
    } = this.props;

    if (message.messageType === 'ad' || message.messageType === 'ext-ad') {
      return 'partner';
    }

    return 'client';
  };

  /**
     * Update the message
     */
  onUpdateVisualFileList = (data) => {
    this.setState({
      newVisualFiles: [...data.newVisualFiles],
    });
  };

  /**
   * When the locations assigned is clicked
   */
  onClickLocations = () => {
    const {
      user,
      message: {
        _id,
        mediaFormat,
        companyId,
        locations,
        locationsData,
      },
      type,
      setPanel,
      onUpdate,
    } = this.props;

    const {
      newVisualFiles,
    } = this.state;

    const allowChanges = user.hasAccessToCompany(companyId)
      && user.can('message.assign_locations')
      && ((_id && user.can('message.modify')) || (!_id && user.can('message.request')));

    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <MessageLocations
            type={type}
            messageId={_id}
            mediaFormat={mediaFormat}
            companyId={companyId}
            locations={locations}
            newVisualFiles={newVisualFiles}
            locationsData={locationsData}
            allowChanges={allowChanges}
            filtersUnassigned={{
              mediaFormat,
            }}
            onUpdate={onUpdate}
            onUpdateVisualFileList={this.onUpdateVisualFileList}
          />
        ),
      },
    });
  };

  /**
   * When the companies assigned is clicked (for ads)
   */
  onClickCompanies = () => {
    const {
      user,
      message: {
        _id,
        locationsData,
        partnerId,
        mediaFormat,
      },
      setPanel,
      onUpdate,
    } = this.props;

    const {
      newVisualFiles,
    } = this.state;

    const allowChanges = ((_id && user.can('advertisement.modify'))
      || (!_id && user.can('advertisement.request')));

    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <MessageCompanies
            partnerId={partnerId}
            mediaFormat={mediaFormat}
            companies={locationsData.companies}
            newVisualFiles={newVisualFiles}
            onUpdateVisualFileList={this.onUpdateVisualFileList}
            allowChanges={allowChanges}
            onUpdate={onUpdate}
          />
        ),
      },
    });
  };

  // /**
  //  * When an attachment is dropped on a new message request
  //  */
  // onLocalAttachment = async (file) => {
  //   const {
  //     message: {
  //       script,
  //     },
  //     onUpdate,
  //   } = this.props;

  //   const {
  //     localAttachments,
  //   } = this.state;

  //   const newLocalAttachments = localAttachments.concat({
  //     fileName: file.name,
  //     fileSizeBytes: file.size,
  //     attachmentId: uniqueId('local-attachment-'),
  //     file,
  //   });

  //   this.setState({
  //     localAttachments: newLocalAttachments,
  //   });

  //   if (script === '') {
  //     onUpdate({
  //       script: 'See attached',
  //     });
  //   }
  // };

  /**
   * When a local attachment is deleted
   */
  onDeleteLocalAttachment = async (attachmentId) => {
    this.setState(state => {
      const localAttachments = state.localAttachments.filter((attachment) => attachment.attachmentId !== attachmentId);

      return {
        localAttachments,
      };
    });
  };

  /**
   * When the message blocks assigned is clicked
   */
  onClickMessageBlocks = async () => {
    const {
      type,
      user,
      message: {
        _id,
        messageType,
        companyId,
        locations,
        messageBlocks = [],
        messageLists = [],
      },
      setPanel,
      onUpdate,
    } = this.props;

    let allowChanges = false;

    if (messageType === 'ad' || messageType === 'ext-ad') {
      // check if changes can be made to ads
      allowChanges = ((_id && user.can('advertisement.modify'))
        || (!_id && user.can('advertisement.request')));
    } else {
      // check if changes can be made for normal messages
      allowChanges = user.hasAccessToCompany(companyId)
        && user.can('message.assign_locations')
        && ((_id && user.can('message.modify'))
          || (!_id && user.can('message.request')));
    }

    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <MessageBlocks
            companyId={companyId}
            locations={locations}
            messageBlocks={type === 'library'
              ? messageBlocks
              : messageLists}
            allowChanges={allowChanges}
            onUpdate={onUpdate}
          />
        ),
      },
    });
  };

  /**
   * When the request changes button is clicked
   */
  onClickRequestChanges = () => {
    this.setState({
      requestChanges: true,
    });
  };

  /**
   * When the request changes is closed
   */
  onCloseRequestChanges = () => {
    this.setState({
      requestChanges: false,
    });

    // tell listening components the message/request was saved
    document.dispatchEvent(new Event('onSaveMessage'));
  };

  /**
   * When the assign voice talent button is clicked
   */
  onClickAssignVoiceTalent = () => {
    this.setState({
      assignVoiceTalent: true,
    });
  };

  /**
   * When the assign voice talent is closed
   */
  onCloseAssignVoiceTalent = (voiceTalent) => {
    const {
      onUpdate,
    } = this.props;

    this.setState({
      assignVoiceTalent: false,
    });

    // ensure the object passed is a voice talent, not a click event handler
    if (voiceTalent && voiceTalent._id) {
      onUpdate({
        voiceTalent,
        voiceTalentId: voiceTalent._id,
        voiceTalentFname: voiceTalent.fname,
        voiceTalentLname: voiceTalent.lname,
        voiceTalentImageUrl: voiceTalent.imageUrl,
      });
    }

    // tell listening components the message/request was saved
    document.dispatchEvent(new Event('onSaveMessage'));
  };

  /**
   * When the upload button is clicked
   */
  onClickUpload = () => {
    this.setState({
      uploadCut: true,
    });
  };

  /**
   * When the upload cut is closed
   */
  onCloseUpload = () => {
    const {
      user,
      message,
      onRefresh,
    } = this.props;

    const isVoiceTalent = user.voiceTalent && user._id === message.voiceTalentId;

    this.setState({
      uploadCut: false,
    });

    // tell listening components the message/request was saved
    document.dispatchEvent(new Event('onSaveMessage'));

    if (!isVoiceTalent) {
      // Do not refresh for voice talent because they can only view rough cuts
      onRefresh();
    }
  };

  /**
   * When the download button is clicked
   */
  onClickDownload = () => {
    this.setState({
      downloadCut: true,
    });
  };

  /**
   * When the download cut is closed
   */
  onCloseDownload = () => {
    this.setState({
      downloadCut: false,
    });
  };

  /**
   * When the archive button is clicked
   */
  onClickArchive = () => {
    this.setState({
      confirmArchive: true,
    });
  };

  /**
   * When the unarchive button is clicked
   */
  onClickUnarchive = async () => {
    const {
      type,
      message: {
        _id,
        locations,
      },
      onClose,
    } = this.props;

    if (type === 'request') {
      await API.MessageRequest.reactivate({
        _id,
        locations,
      });
    } else {
      await API.Message.reactivate({
        _id,
        locations,
      });
    }

    onClose();

    // tell listening components the message/request was saved
    document.dispatchEvent(new Event('onSaveMessage'));
  };

  /**
   * When the archive modal is closed
   */
  onCloseArchive = () => {
    this.setState({
      confirmArchive: false,
    });
  };

  /**
   * When the approval confirmation is closed
   */
  onCloseApprovalModal = () => {
    this.setState({
      savedAdditionalVisualFiles: false,
    });
  };

  /**
   * When the no message block modal is closed
   */
  onConfirmAndSend = () => {
    this.setState({
      showConfirmNoMB: false,
    });

    this.onSave({
      confirmNoMB: true,
    });
  };

  /**
   * When the no message block modal is closed
   */
  onCancelAndMsgBlocks = () => {
    this.setState({
      showConfirmNoMB: false,
    });

    this.onClickMessageBlocks();
  };

  /**
   * When the archive modal is confirmed
   */
  onConfirmArchive = async () => {
    const {
      type,
      message: {
        _id,
      },
      onClose,
    } = this.props;

    if (type === 'request') {
      await API.MessageRequest.deactivate(_id);
    } else {
      await API.Message.deactivate(_id);
    }

    this.onCloseArchive();
    onClose();

    // tell listening components the message/request was saved
    document.dispatchEvent(new Event('onSaveMessage'));
  };

  /**
   * When the approve button is clicked
   */
  onClickApprove = () => {
    this.setState({
      confirmApprove: true,
    });
  };

  /**
   * When the approve modal is closed
   */
  onCloseApprove = () => {
    this.setState({
      confirmApprove: false,
    });
  };

  /**
   * When the approve message modal is confirmed
   */
  onConfirmApprove = async () => {
    const {
      user,
      message: {
        _id,
      },
      onRefresh,
    } = this.props;

    const response = await API.MessageRequest.Approval.approve({
      _id,
      response: 'accept',
      approvalType: user.accountType === 'partner'
        ? 'partner'
        : 'client',
    });

    const success = get(response, '[0].type') === 'MESSAGEREQUEST.APPROVED'
      || get(response, '[0].type') === 'MESSAGEREQUEST.PARTIALLY_APPROVED';

    if (success) {
      // tell listening components the message/request was saved
      document.dispatchEvent(new Event('onSaveMessage'));
      this.onCloseApprove();
      onRefresh();
    }
  };

  /**
   * When the override approve button is clicked
   */
  onClickApproveOverride = () => {
    this.setState({
      confirmApproveOverride: true,
    });
  };

  /**
   * When the approve modal is closed
   */
  onCloseApproveOverride = () => {
    this.setState({
      confirmApproveOverride: false,
    });
  };

  /**
   * When the approve override modal is confirmed
   */
  onConfirmApproveOverride = async () => {
    const {
      message: {
        _id,
      },
      user,
      onRefresh,
    } = this.props;

    const response = await API.MessageRequest.Approval.approve({
      _id,
      response: 'accept',
      approvalType: user.accountType === 'partner' ? 'override-partner' : 'override-global',
    });

    const success = get(response, '[0].type') === 'MESSAGEREQUEST.APPROVED'
      || get(response, '[0].type') === 'MESSAGEREQUEST.PARTIALLY_APPROVED';

    if (success) {
      // tell listening components the message/request was saved
      document.dispatchEvent(new Event('onSaveMessage'));
      this.onCloseApproveOverride();
      onRefresh();
    }
  };

  /**
   * User does not auto remove the invalid message blocks
   */
  onCancelRemoveInvalidMessageBlocks = () => {
    this.setState({
      showInvalidMessageBlocks: false,
      invalidMessageBlocks: [],
    });
  };

  /**
   * User wants to automatically remove the invalid message blocks
   */
  onConfirmRemoveInvalidMessageBlocks = async () => {
    const {
      type,
      message: {
        _id,
        messageLists,
        messageBlocks,
      },
      onUpdate,
    } = this.props;

    const {
      invalidMessageBlocks,
    } = this.state;

    // message library uses messageBlocks and requests use messageLists
    const blocks = type === 'request'
      ? messageLists
      : messageBlocks;

    const newMessageBlocks = [];

    blocks.forEach((block) => {
      const isInvalid = find(invalidMessageBlocks, { _id: block._id }) !== undefined;

      if (!isInvalid) {
        newMessageBlocks.push(block);
      }
    });

    const data = type === 'request'
      ? {
        messageLists: newMessageBlocks,
      }
      : {
        messageBlocks: newMessageBlocks,
      };

    onUpdate(data);

    // change the selected message blocks
    const updateMessageBlocksResponse = type === 'request'
      // update message request message blocks
      ? await API.MessageRequest.changeMessageBlocks({
        _id,
        messageLists: newMessageBlocks.map(block => block._id),
      })
      // update message library message blocks
      : await API.Message.changeMessageBlocks({
        _id,
        messageBlocks: newMessageBlocks.map(block => block._id),
      });

    // did the message blocks get removed from the message
    const updateMessageBlocksSuccess = type === 'request'
      ? get(updateMessageBlocksResponse, '[0].type') === 'MESSAGEREQUEST.MESSAGELISTS_CHANGED'
      : get(updateMessageBlocksResponse, '[0].type') === 'MESSAGE.MESSAGE_BLOCKS_UPDATED';

    if (updateMessageBlocksSuccess) {
      // update the message with the new location selection / message block assignments
      this.onSave({
        // skip the message block update call since it just occurred
        skipMessageBlockUpdate: true,
      });
    }

    this.setState({
      showInvalidMessageBlocks: false,
      invalidMessageBlocks: [],
    });
  };

  /**
   * Save the Message
   */
  onSave = async (options = {}) => {
    const {
      message: {
        _id,
        // requestId,
        name,
        messageType,
        companyId,
        advertiserId,
        category,
        locations,
        trackingUrls,
        mediaFormat,
        messageBlocks = [],
        messageLists = [],
        tags,
        readType,
        script,
        instructions,
        startDate,
        endDate,
        dueDate,
        publishMode,
        file,
        daysOfWeek,
        clientApproval,
        partnerApproval,
      },
      // user,
      message,
      type,
      isNew,
      isDuplicate,
      isCopy,
      isReRequest,
      history,
      queueToast,
      onUpdate,
      onRefresh,
    } = this.props;

    const {
      localAttachments,
      // partnerData,
      partnerId,
      partnerName,
      newVisualFiles,
    } = this.state;

    const {
      updated,
    } = this;

    // custom data to send to the message create/update
    const customData = {};

    if (message.messageType === 'ad' || message.messageType === 'ext-ad') {
      // include tracking URLs for Ad message types
      customData.trackingUrls = trackingUrls;
      // include ad category
      customData.category = category;
    }

    const invalidMessageBlocks = this.validateMessageBlocks();

    if (invalidMessageBlocks.length > 0) {
      this.setState({
        showInvalidMessageBlocks: true,
        invalidMessageBlocks,
      });

      return;
    }

    let filteredVisualFiles = [];
    if (message.mediaFormat === 'visual') {
      // Check locations for resolution changes before saving
      const locationResolutions = locations.length > 0 ? await API.Location.resolutions({
        locations,
      }) : [];

      let resolutionError = false;

      for (let i = 0; i < locationResolutions.length; i++) {
        resolutionError = message.resolutions.some(resolution => {
          if ((locationResolutions[i].size[0] === resolution.size[0])
            && (locationResolutions[i].size[1] === resolution.size[1])) {
            return true;
          } return false;
        });
        if (!resolutionError) break;
      }

      // Remove visual uploads that no longer apply
      filteredVisualFiles = newVisualFiles.filter((file) => {
        const hasMatch = locationResolutions.some((resolution) => {
          if ((file.targetScreen.resolution[0] === resolution.size[0])
            && (file.targetScreen.resolution[1] === resolution.size[1])) {
            return true;
          } return false;
        });
        return hasMatch;
      });

      if (!resolutionError) {
        this.setState({
          resolutionErrors: true,
          newVisualFiles: filteredVisualFiles,
        });
        onUpdate({
          resolutions: locationResolutions,
        });
      } else {
        this.setState({
          resolutionErrors: false,
        });
      }
    }

    if (isNew
        // save with no message blocks if they've confirmed
        && (size(messageBlocks) <= 0 && !options.confirmNoMB)
        && messageType !== 'ad'
        && messageType !== 'ext-ad'
        && messageType !== 'air'
        && messageType !== 'spec'
        && mediaFormat === 'audio'
    ) {
      this.setState({
        showConfirmNoMB: true,
      });

      return;
    }

    document.dispatchEvent(new Event('onSaveMessageStart'));

    // Get tags to add/remove
    const modifyTags = tags.filter(tag => tag.status === 'add' || tag.status === 'remove');
    // is this a message request we're updating?
    const isUpdateMessageRequest = type === 'request' && !isNew && !options.isDuplicate;
    let messageResponse;

    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      if (isNew || isDuplicate || isCopy) {
        // Create a message request
        // get the approver settings based on the approverType (client, partner)
        const approverObject = this.getApproverType() === 'client'
          ? 'clientApproval'
          : 'partnerApproval';

        const approverSettings = get(message, approverObject, {}) || {};

        if (publishMode === 'review') {
          // only include approver data if the message requires approver
          customData[approverObject] = approverSettings.style === 'anyone' ? {
            style: 'anyone',
            approvers: [],
          } : {
            ...approverSettings,
            approvers: approverSettings.approvers.map((approver) => {
              return {
                _id: approver._id,
                approvalType: this.getApproverType(),
              };
            }),
          };
        }

        messageResponse = await API.MessageRequest.create({
          // originalRequestId: requestId,
          mediaSourceRequestId: message.mediaSourceRequestId,
          name,
          locations,
          publishMode,
          messageType,
          advertiserId,
          companyId,
          mediaFormat,
          partnerId,
          partnerName,
          script,
          instructions,
          messageLists: messageBlocks.map(messageBlock => messageBlock._id),
          readType,
          startDate: startDate
            ? new moment(startDate).format('YYYY-MM-DD')
            : '',
          endDate: endDate
            ? new moment(endDate).format('YYYY-MM-DD')
            : '',
          // only include a due date if not an upload
          dueDate: dueDate && message.messageType !== 'ext' && message.messageType !== 'ext-ad'
            ? new moment(dueDate).format('YYYY-MM-DD')
            : '',
          daysOfWeek,
          tags: modifyTags.map((tag) => {
            return {
              _id: tag._id,
              action: tag.status,
            };
          }),
          ...customData,
        });
      } else if (isUpdateMessageRequest) {
        // Update a message request
        if (updated.name) {
          // Update the message name
          const response = await API.MessageRequest.rename({
            _id,
            name,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.RENAMED';

          if (success) {
            this.origMessage.name = name;

            queueToast({
              type: 'success',
              title: 'Name Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.publishMode) {
          // Update the message publish mode
          const data = {
            _id,
            publishMode,
          };

          if (!isEmpty(clientApproval) && publishMode === 'review') {
            data.clientApproval = clientApproval;
          }

          if (!isEmpty(partnerApproval) && publishMode === 'review') {
            data.partnerApproval = partnerApproval;
          }

          const response = await API.MessageRequest.changePublishMode(data);

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.PUBLISH_MODE_CHANGED';

          if (success) {
            this.origMessage.publishMode = publishMode;
            this.origMessage.partnerApproval = message.partnerApproval;
            this.origMessage.clientApproval = message.clientApproval;
            updated.publishMode = false;

            queueToast({
              type: 'success',
              title: 'Publish Mode Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.script) {
          // Update the message script
          const response = await API.MessageRequest.changeScript({
            _id,
            script,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.SCRIPT_CHANGED';

          if (success) {
            this.origMessage.script = script;

            queueToast({
              type: 'success',
              title: 'Script Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.instructions) {
          // Update the message special instructions
          const response = await API.MessageRequest.changeInstructions({
            _id,
            instructions,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.INSTRUCTIONS_CHANGED';

          if (success) {
            this.origMessage.instructions = instructions;

            queueToast({
              type: 'success',
              title: 'Instructions Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.dates || updated.daysOfWeek) {
          // Update the message dates
          const response = await API.MessageRequest.changeDates({
            _id,
            startDate: new moment(startDate).format('YYYY-MM-DD'),
            endDate: new moment(endDate).format('YYYY-MM-DD'),
            dueDate: new moment(dueDate).format('YYYY-MM-DD'),
            daysOfWeek,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.DATES_CHANGED';

          if (success) {
            this.origMessage.startDate = startDate;
            this.origMessage.endDate = endDate;
            this.origMessage.dueDate = dueDate;
            this.origMessage.daysOfWeek = daysOfWeek;

            queueToast({
              type: 'success',
              title: 'Dates Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.tags) {
          // Update the message tags
          const response = await API.MessageRequest.changeTags({
            _id,
            tags: modifyTags.map((tag) => {
              return {
                _id: tag._id,
                action: tag.status,
              };
            }),
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.TAGS_CHANGED';

          if (success) {
            this.origMessage.tags = modifyTags;

            queueToast({
              type: 'success',
              title: 'Tags Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        // Update the message blocks for a message request
        // do not update if the option is set (when message blocks are removed due to a location change)
        if (updated.messageLists && !options.skipMessageBlockUpdate) {
          const response = await API.MessageRequest.changeMessageBlocks({
            _id,
            messageLists: messageLists.map(messageBlock => messageBlock._id),
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.MESSAGELISTS_CHANGED';

          if (success) {
            this.origMessage.messageLists = messageLists;

            queueToast({
              type: 'success',
              title: 'Message Blocks Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.locations) {
          const response = await API.MessageRequest.changeLocations({
            _id,
            locations,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.LOCATIONS_CHANGED';

          if (success) {
            this.origMessage.locations = locations;

            queueToast({
              type: 'success',
              title: 'Locations Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.trackingUrls && (message.messageType === 'ad' || message.messageType === 'ext-ad')) {
          const response = await API.MessageRequest.changeTrackingUrls({
            _id,
            trackingUrls,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.TRACKINGURLS_CHANGED';

          if (success) {
            this.origMessage.trackingUrls = trackingUrls;

            queueToast({
              type: 'success',
              title: 'Tracking URLs Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }

        if (updated.category && (message.messageType === 'ad' || message.messageType === 'ext-ad')) {
          const response = await API.MessageRequest.changeCategory({
            _id,
            category,
          });

          const success = get(response, '[0].type') === 'MESSAGEREQUEST.CATEGORY_CHANGED';

          if (success) {
            this.origMessage.category = category;

            queueToast({
              type: 'success',
              title: 'Category Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }
      } else {
        // Update a message
        messageResponse = await API.Message.update({
          _id,
          name,
          locations,
          startDate: startDate
            ? new moment(startDate).format('YYYY-MM-DD')
            : '',
          endDate: endDate
            ? new moment(endDate).format('YYYY-MM-DD')
            : '',
          dueDate: dueDate
            ? new moment(dueDate).format('YYYY-MM-DD')
            : '',
          daysOfWeek,
          tags: modifyTags.map((tag) => {
            return {
              _id: tag._id,
              action: tag.status,
            };
          }),
          ...customData,
        });

        // Update the message blocks for a message in the library
        // do not update if the option is set (when message blocks are removed due to a location change)
        if (updated.messageLists && !options.skipMessageBlockUpdate) {
          const response = await API.Message.changeMessageBlocks({
            _id,
            messageBlocks: messageBlocks.map(messageBlock => messageBlock._id),
          });

          const success = get(response, '[0].type') === 'MESSAGE.MESSAGE_BLOCKS_UPDATED';

          if (success) {
            this.origMessage.messageLists = messageLists;

            queueToast({
              type: 'success',
              title: 'Message Blocks Changed!',
              allowClose: true,
              delay: 500,
            });
          }
        }
      }

      const newMessageId = get(messageResponse, '[0].documentId', _id);
      const messageRequestId = message.requestId ?? newMessageId;

      if (localAttachments.length > 0 && newMessageId) {
        localAttachments.forEach(async attachment => {
          const uploadResponse = await API.MessageRequest.uploadAttachment(newMessageId, attachment.file);

          const uploadId = get(uploadResponse, '[0].documentId', null);

          if (uploadId) {
            this.onDeleteLocalAttachment(attachment.attachmentId);
          }
        });
      }

      if (!isEmpty(filteredVisualFiles)) {
        // Upload Visual File(s)
        const visualMediaRequests = await Promise.all(filteredVisualFiles.map(async (visualfile) => {
          const visualMediaRequest = API.MessageRequest.uploadFinalVisual(
            newMessageId,
            visualfile.file,
            visualfile.targetScreen,
            visualfile.onProgress
          );

          return Promise.resolve(visualMediaRequest);
        }));

        const errorNewVisualFiles = filteredVisualFiles.filter((visualRequest, i) => {
          if (visualMediaRequests[i].error) {
            return true;
          }
          return false;
        }).map((visFile) => {
          visFile.isError = true;
          return visFile;
        });

        this.setState({
          newVisualFiles: errorNewVisualFiles,
          savedAdditionalVisualFiles: true,
        });

        onUpdate({
          messageId: newMessageId,
        });
      } else if (file) {
        // no visual files to upload, assume audio file
        // Upload message
        await API.MessageRequest.uploadFinal(newMessageId, file);
      }

      if (newMessageId || isUpdateMessageRequest) {
        // Created or updated a message
        queueToast({
          type: 'success',
          title: 'Saved!',
          allowClose: true,
          delay: 500,
        });

        if (isUpdateMessageRequest) {
          // Reset the updated fields
          this.updated = {};
        }

        // Refresh the message details
        if ((isNew || isCopy) && !isReRequest) {
          const redirectUrl = NavigationHelper.updateParams({
            messageId: newMessageId,
            type: null,
          });

          history(redirectUrl);
        } else if (isReRequest) {
          // Re-Requesting a message from the library. Redirect to the message requests page
          const redirectUrl = NavigationHelper.updateParams({
            messageId: newMessageId,
            type: null,
          }, {
            pathname: `/messages/requests`,
          });

          history(redirectUrl);
        }
      } else {
        queueToast({
          type: 'error',
          title: 'Error Saving Message',
          timeout: 10,
          allowClose: true,
        });
      }

      // tell listening components the message/request was saved
      document.dispatchEvent(new Event('onSaveMessage'));
    } catch (err) {
      console.error('Error Saving Message', err);
      document.dispatchEvent(new Event('onSaveMessageError'));
    }
  };

  onClickApprover = () => {
    this.setState({
      assignApprover: true,
    });
  };

  /**
   * When the approver list is closed
   */
  onCloseApprovers = () => {
    this.setState({
      assignApprover: false,
    });
  };

  onClickEmailApprover = async () => {
    this.setState({
      showEmailApprover: true,
    });
  };

  onChooseApproverToEmail = async (approverId) => {
    const {
      message: {
        _id,
      },
      queueToast,
    } = this.props;

    try {
      queueToast({
        type: 'info',
        title: 'Emailing Approver...',
        allowClose: true,
      });

      const response = await API.MessageRequest.sendApprovalNotification(_id, {
        _id,
        approverId,
      });

      const success = get(response, '[0].type') === 'MESSAGEREQUEST.NOTIFY_APPROVER_STARTED';

      if (success) {
        queueToast({
          type: 'success',
          title: 'Email Sent!',
          allowClose: true,
          delay: 500,
        });
      } else {
        queueToast({
          type: 'error',
          title: 'Error Emailing Approver',
          timeout: 10,
          allowClose: true,
        });
      }
    } catch (err) {
      queueToast({
        type: 'error',
        title: 'Error Emailing Approver',
        timeout: 10,
        allowClose: true,
      });
    }
  };

  onCloseApproverEmail = () => {
    this.setState({
      showEmailApprover: false,
    });
  };

  onReRequest = () => {
    const {
      message,
      onUpdate,
    } = this.props;

    const defaultDate = this.calculateDueDate();

    const approverObject = this.getApproverType() === 'client'
      ? 'clientApproval'
      : 'partnerApproval';

    // approvers to use (remove any override approvers)
    const approvers = (get(message, `${approverObject}.approvers`, []) || [])
      .filter(approver => approver.approvalType !== 'override-client'
        && approver.approvalType !== 'override-global'
        && approver.approvalType !== 'override-partner')
      .map((approver) => {
        return {
          ...approver,
          // reset the approved time and approval status
          reviewedAt: '',
          status: '',
        };
      });

    // reset data needed for a new message request
    onUpdate({
      dueDate: defaultDate,
      messageLists: [],
      tags: message.tags.map(tag => {
        return {
          ...tag,
          status: 'add',
        };
      }),
      [approverObject]: {
        ...message[approverObject],
        approvers,
      },
    });
  };

  /**
   * Validate the message has at least one location overlap with each assigned message block
   */
  validateMessageBlocks = () => {
    const {
      type,
      message: {
        locations,
        messageLists = [],
        messageBlocks = [],
      },
    } = this.props;

    // message library uses messageBlocks and requests use messageLists
    const blocks = type === 'request'
      ? messageLists
      : messageBlocks;

    const invalidMessageBlocks = [];

    if (blocks.length <= 0) {
      return [];
    }

    blocks.forEach((block) => {
      const hasOverlap = LocationHelper.overlap(locations, block.locations);

      if (!hasOverlap) {
        // the message block is no longer valid for this message based on the location selection
        invalidMessageBlocks.push(block);
      }
    });

    return sortBy(invalidMessageBlocks, 'name');
  };

  calculateDueDate = () => {
    // Default Due Date = +3 Business Days From Today
    let daysToAdd = 3;
    const dayBefore = moment().day();
    const dayAfter = moment().add(daysToAdd, 'days').day();

    // If rolled over to the next week or Saturday, skip the weekend
    if (dayBefore > dayAfter || dayAfter === 6) {
      daysToAdd += 2;
    }

    const defaultDate = moment().add(daysToAdd, 'days').endOf('day').format();

    return defaultDate;
  };

  getMediaArray = (message) => {
    let activity = cloneDeep(message.activity.filter(activity => activity.activityType === 'upload'));

    if (activity.length <= 0) {
      return [];
    }

    let mediaArray = [];

    if (message.mediaFormat === 'audio' || !message.mediaFormat) {
      activity = sortBy(activity, a => a.date);
      mediaArray = activity.length === 0
        ? activity
        : [activity.at(activity.length - 1).detail];
    } else {
      activity = activity.map(a => {
        const { resolution, orientation } = a.detail.targetScreen;
        if (!resolution || resolution.length <= 0) {
          a.targetScreen = '';
          return a;
        }
        a.targetScreen = `${resolution[0]}x${resolution[1]} ${orientation}`;
        return a;
      });
      activity = groupBy(activity, 'targetScreen');
      const mostRecentByTargetScreen = values(mapValues(activity, a => maxBy(a, 'date')?.detail));
      mediaArray = mostRecentByTargetScreen;
    }

    return mediaArray;
  };

  getSaveMessage = () => {
    const {
      isNew,
      isCopy,
      copying,
    } = this.props;
    if (isNew) {
      return 'Send Request';
    }

    if (isCopy && copying) {
      return 'Save Copy';
    }
    return 'Save Changes';
  };

  /**
   * Determine if the publishing options should be shown
   */
  getShowPublishingOptions = () => {
    const {
      type,
      message,
    } = this.props;

    switch (message.messageType) {
      case 'ext-ad':
        return false;

      case 'ad': {
        if (type === 'request') {
          return true;
        }

        return false;
      }

      default:
        return true;
    }
  };

  render() {
    const {
      user,
      message,
      type,
      isNew,
      isCopy,
      isReRequest,
      copying,
      showDisplayPlayer,
      onToggleChat,
      onToggleHistory,
      onClose,
      onUpdate,
      onRefresh,
      // isDuplicate,
    } = this.props;

    const {
      requestChanges,
      assignVoiceTalent,
      assignApprover,
      downloadCut,
      uploadCut,
      confirmArchive,
      confirmApprove,
      confirmApproveOverride,
      showConfirmNoMB,
      // localAttachments,
      showEmailApprover,
      showInvalidMessageBlocks,
      invalidMessageBlocks,
      newVisualFiles,
      savedAdditionalVisualFiles,
      // requireAdApproval,
      resolutionErrors,
    } = this.state;

    const mediaFormat = get(message, 'mediaFormat', null) || null;

    const isContentCurator = user.can('message.content_curator');
    const isVoiceTalent = user.voiceTalent && user._id === message.voiceTalentId;

    // get the approver settings and list based on the approverType (client, partner)
    const approverType = this.getApproverType();
    const approverSettings = get(message, `${approverType}Approval`, {}) || {};
    const approvers = approverSettings.approvers || [];

    // ACAF match
    const isACAF = user.isAllLocations({
      companyId: message.companyId,
      locations: message.locations,
    });

    const locationCount = LocationHelper.getLocationCount(get(message, 'locationsData.companies', []), isACAF);
    const messageBlockCount = type === 'library'
      ? get(message, 'messageBlocks.length', 0)
      : get(message, 'messageLists.length', 0);
    const exceedsLocations = user.exceedsLocations(message.locations);

    const isUpload = message.messageType === 'ext'
      || message.messageType === 'ext-ad';

    message.combinedVisualFiles = [];

    if (mediaFormat === 'visual' && type === 'request') {
      // find latest file that matches each resolution
      const resolutions = [];
      const visualFiles = message.activity.filter(activity => activity.activityType === 'upload').map(activity => {
        const newVisualFile = {
          file: {
            path: activity.detail.url,
            name: activity.detail.fileName,
            size: activity.detail.fileSizeBytes,
            type: activity.detail.contentType,
            durationSeconds: activity.detail.durationSeconds,
            width: activity.detail.resolution[0],
            height: activity.detail.resolution[1],
          },
          date: activity.date,
          isError: false,
          targetScreen: activity.detail.targetScreen,
        };
        resolutions.push(activity.detail.targetScreen.resolution);
        return newVisualFile;
      });

      const latestVisualFilePerResolution = message.resolutions.map(uniqRes => {
        let latestVisualFile = {};
        visualFiles.forEach((visFile) => {
          if ((uniqRes.size[0] === visFile.targetScreen.resolution[0])
            && (uniqRes.size[1] === visFile.targetScreen.resolution[1])
            && (uniqRes.orientation === visFile.targetScreen.orientation))
          {
            if (isEmpty(latestVisualFile)) {
              latestVisualFile = visFile;
            }
            if (new Date(visFile.date) > new Date(latestVisualFile.date)) {
              latestVisualFile = visFile;
            }
          }
        });
        return latestVisualFile;
      });

      // if a newVisualFile overrides a currently saved one, remove it from the latestVisualFilePerResolution
      // ALSO, make sure they match the currently selected locations...

      const filteredLatestVisualFiles = latestVisualFilePerResolution.filter(visFile => {
        if (isEmpty(visFile)) return false;
        /* eslint-disable-next-line max-len */
        return !newVisualFiles.some(visfile1 => (visfile1.targetScreen.resolution[0] === visFile.targetScreen.resolution[0])
          && (visfile1.targetScreen.resolution[0] === visFile.targetScreen.resolution[0])
          && (visfile1.targetScreen.orientation === visFile.targetScreen.orientation),
        );
      });

      message.combinedVisualFiles = [
        ...filteredLatestVisualFiles,
        ...newVisualFiles,
      ];
    }

    if (message.status === 'completed' && !isEmpty(message.media)) {
      message.combinedVisualFiles = newVisualFiles.length > 0
        ? newVisualFiles
        : message.media.map((mediaitem) => {
            return {
              file: {
                path: mediaitem.url,
                name: mediaitem.fileName,
                size: mediaitem.fileSizeBytes,
                type: mediaitem.contentType,
                durationSeconds: mediaitem.durationSeconds,
                width: mediaitem.resolution === undefined ? '' : mediaitem.resolution[0],
                height: mediaitem.resolution === undefined ? '' : mediaitem.resolution[1],
              },
              date: '',
              isError: false,
              targetScreen: mediaitem.targetScreen,
            };
          });
    }

    const visualErrors = message.combinedVisualFiles.some(visFile => visFile.isError);

    const getShowCopy = () => {
      const {
        mediaFormat,
      } = this.props;

      const canMessageUpload = user.can('message.upload');
      const canAdvertisementUpload = user.can('advertisement.upload');
      const canAdvertisementRequest = user.can('advertisement.request');
      const isAudioMessage = mediaFormat === 'audio';

      const messageTypeOptions = [];

      switch (user.accountType) {
        case 'standard':
          messageTypeOptions.push('std');
          if (canMessageUpload) {
          // Allow the user to upload a Self Promo message
            messageTypeOptions.push('ext');
          }

          if (user.isAdminCompany) {
            if (isAudioMessage) {
              // Allow user to create an Air Check
              messageTypeOptions.push('air');

              // Allow user to create an Spec Check
              messageTypeOptions.push('spec');
            }
          }
          break;

        case 'partner':
          if (canAdvertisementRequest) {
            // Partner User that can create ads
            messageTypeOptions.push('ad');
          }

          if (canAdvertisementUpload) {
            // Allow the user to upload an ad
            messageTypeOptions.push('ext-ad');
          }

          break;
        default:
          break;
      }

      return messageTypeOptions.includes(message.messageType);
    };

    const disableSave = !message.name
      || !message.companyId
      || !message.messageType
      // File upload is required when creating an upload request
      || (isNew
          && message.mediaFormat === 'audio'
          && isUpload
          && isUndefined(message.file))
      // Script not required when a file has been uploaded or editing and existing message
      || (isNew
        && !isUpload
        && !message.script)
      || message.locations.length <= 0
      // Require due date for message requests (except for uploads)
      || (type === 'request' && !isUpload && !message.dueDate)
      // Require an advertiser for ad messages
      || ((message.messageType === 'ad' || message.messageType === 'ext-ad') && !message.advertiserId)
      // Message requires approval (one approver must be selected) and if approver style is not 'anyone'
      || ((message.requireApproval || message.publishMode === 'review')
        && approvers.length <= 0 && approverSettings.style !== 'anyone')
      // Message has approvers selected but no approver setting selected
      || (message.status !== 'completed'
        && approvers.length > 0
        && !approverSettings.style
        && message.publishMode === 'review')
      || visualErrors;

    let disableInput = false;

    if (user.accountType === 'partner') {
      // check if input is disabled for ads
      disableInput = (message._id && !user.can('advertisement.modify'))
        || (!message._id && !user.can('advertisement.request'));
    } else {
      // check if input is disabled for normal messages
      disableInput = !user.hasAccessToCompany(message.companyId)
        || !exceedsLocations
        || (message._id && !user.can('message.modify'))
        || (!message._id && !user.can('message.request'));
    }

    // disable the assignment sections if not filled in
    const disableLocations = disableInput
      || message.messageType === 'air'
      || message.messageType === 'spec'
      || (!message.companyId && !message.partnerId);

    const disableMessageBlocks = disableInput
      || message.locations.length <= 0
      || message.messageType === 'ext-ad'
      || message.messageType === 'air'
      || message.messageType === 'spec'
      || !user.can('messagelist.view');

    const isAd = message.messageType === 'ad'
      || message.messageType === 'ext-ad';

    const showReRequest = type === 'library'
      && ((!isAd && user.can('message.request'))
          || (isAd && user.can('advertisement.request')))
      && exceedsLocations
      // prevent regular and ad uploads from being re-requested
      && (message.messageType !== 'ext' && message.messageType !== 'ext-ad');

    const showApproveActions = approvers.length > 0
      ? find(approvers, { _id: user._id }) !== undefined
        && ((!isAd && user.can('message.approve'))
          || (isAd && user.accountType === 'partner' && user.can('advertisement.approve')))
        && message.status === 'final-cut-uploaded'
        && exceedsLocations
      : ((!isAd && user.can('message.approve'))
          || (isAd && user.accountType === 'partner' && user.can('advertisement.approve')))
        && message.status === 'final-cut-uploaded'
        && exceedsLocations;

    // show the request changes button
    const showRequestChanges = showApproveActions
      && message.messageType !== 'ext'
      && message.messageType !== 'ext-ad';

    // show the deny button
    const showDeny = showApproveActions
      && (message.messageType === 'ext' || message.messageType === 'ext-ad') && !copying;

    const showApproveOverrideAction = ((!isAd && user.can('message.global_approver'))
      || (isAd && user.accountType === 'partner' && user.can('advertisement.global_approver')))
        && message.status === 'final-cut-uploaded'
        && exceedsLocations && !copying;

    const showEmailAction = isContentCurator
      && message.status === 'final-cut-uploaded';

    const showDownload = isContentCurator
      ? message.roughCutUrl || message.finalCutUrl
      : message.finalCutUrl && !copying;

    const showCopy = type !== 'library' && getShowCopy() && !copying;

    const showUpload = (isContentCurator || isVoiceTalent)
      && message.status !== 'completed'
      && message.status !== 'final-cut-uploaded';

    let showArchive = isAd
      ? user.can('advertisement.delete')
      : user.can('message.delete');

    // do not allow archive if the user is not part of the company or doesn't exceed the location scope
    if (!isAd && (!showArchive || !user.hasAccessToCompany(message.companyId) || !exceedsLocations)) {
      showArchive = false;
    }

    const isArchived = !message.active;

    const isScriptVisible = message.messageType !== 'ext';

    // tooltip to show for the upload button
    const uploadTooltip = ['Upload'];

    /**
     * disable the upload button when
     * - all approvers must approve
     * - one approver has already approved the message
     */
    const disableUpload = message.publishMode === 'review'
      && approverSettings.style === 'unanimous'
      && find(approvers, { status: 'approved' }) !== undefined;

    if (disableUpload) {
      // add the message why you cannot upload to the disabled button
      uploadTooltip.push('An approver has already approved this message');
    }

    const assignmentsRequired = !isEmpty(message.mediaSourceRequestId) && message.locations.length === 0;
    const approversRequired = !isEmpty(message.mediaSourceRequestId)
      && isEmpty(message.partnerApproval?.approvers || []);

    const icons = (
      <div>
        {!isNew ? (
          <VibeIcon
            id="chat"
            icon={viChat}
            color={message.changeRequested
              ? color.orange
              : color.manatee}
            hoverColor={color.obsidian}
            tooltip="Chat"
            size={24}
            onClick={onToggleChat}
          />
        ) : null}

        {isContentCurator && !isNew ? (
          <VibeIcon
            id="history"
            icon={viTimeHistory}
            color={color.manatee}
            hoverColor={color.obsidian}
            tooltip="History"
            size={24}
            onClick={onToggleHistory}
          />
        ) : null}

        <VibeIcon
          id="close-sidebar"
          className="close"
          icon={viClose}
          color={color.manatee}
          hoverColor={color.obsidian}
          size={24}
          onClick={onClose}
        />
      </div>
    );

    const assignmentOptions = [];

    if (isAd) {
      // Ads use Company Selection
      assignmentOptions.push({
        label: 'Companies Assigned',
        count: locationCount.display.element,
        tooltip: locationCount.display.tooltip,
        required: true,
        disabled: disableLocations,
        warning: !exceedsLocations
          ? 'You do not have access to all the companies assigned to this object'
          : null,
        onClick: !disableLocations
          ? this.onClickCompanies
          : null,
      });
    } else {
      // Regular Messages use Location Selection
      assignmentOptions.push({
        label: 'Locations Assigned',
        count: locationCount.display.element,
        tooltip: locationCount.display.tooltip,
        required: true,
        disabled: disableLocations,
        warning: !exceedsLocations
          ? 'You do not have access to all the locations assigned to this object'
          : null,
        onClick: !disableLocations
          ? this.onClickLocations
          : null,
      });
    }

    // Message Blocks are no longer relevant for any Ad Type Messages
    // So don't show the assignment option if irrelevant
    if (!isAd && message.mediaFormat === 'audio') {
      assignmentOptions.push({
        label: 'Message Blocks Assigned',
        count: messageBlockCount,
        disabled: disableMessageBlocks,
        warning: !user.can('messagelist.view')
          ? 'You do not have permission to view message blocks'
          : null,
        onClick: !disableMessageBlocks
          ? this.onClickMessageBlocks
          : null,
      });
    }

    if (!message.media || (message.media.length <= 0 && message.activity.length > 0)) {
      message.media = this.getMediaArray(message);
    }

    if (isEmpty(message.trackingUrls)) {
      message.trackingUrls = [''];
    }

    return (
      <SidePanelContainer className="MessageDetails">
        <SidePanelHeader
          icons={icons}
        >
          {message._id || message.name ? (
            <div className="flex-horizontal">
              {message.mediaFormat === 'audio'
                ? (
                  <PlayControl
                    id={message._id}
                    name={message.name}
                    src={message.media[0]?.url ?? ''}
                    durationSeconds={message.media[0]?.durationSeconds ?? 0}
                    color={color.success}
                    playText="Play Message"
                    pauseText="Pause Message"
                    disabled={message.media.length === 0}
                    size={24}
                  />
                ) : (
                  <VibeIcon
                    icon={viPlayCircleOutline}
                    color={color.success}
                    hoverColor={color.teal}
                    size={24}
                    style={{
                      marginRight: 4,
                    }}
                    tooltip="Preview Loop"
                    tooltipProps={{
                      placement: 'top',
                    }}
                    disabled={message.media.length === 0}
                    onClick={() => showDisplayPlayer([message])}
                  />
                )}

              <div className="title">
                <VibeTooltip title={message.name}>
                  <span>
                    {message.name}
                  </span>
                </VibeTooltip>
              </div>
            </div>
          ) : (
            <div className="title">
              New Message Request
            </div>
          )}
        </SidePanelHeader>

        <SidePanelContent>
          {!isVoiceTalent && (
            <SidePanelSection title="Type">
              <MediaType
                mediaFormat={mediaFormat}
                type={message.messageType}
                voiceTalentId={message.voiceTalentId}
                disableInput={disableInput}
                isNew={isNew}
                isCopy={isCopy}
                onUpdate={onUpdate}
              />
            </SidePanelSection>
          )}

          {message.mediaFormat && (message.messageType || isVoiceTalent) && (
            <div>
              <SidePanelSection title="Information">
                <Information
                  name={message.name}
                  type={message.messageType}
                  mediaFormat={message.mediaFormat}
                  companyId={message.companyId}
                  companyName={message.companyName}
                  partnerName={message.partnerName}
                  advertiserName={message.advertiserName}
                  category={message.category}
                  tags={message.tags}
                  voiceTalentId={message.voiceTalentId}
                  approverType={approverType}
                  disableInput={disableInput}
                  isNew={isNew}
                  isCopy={isCopy}
                  onUpdate={onUpdate}
                />
              </SidePanelSection>

              {message.messageType && (
                <Assignments
                  items={assignmentOptions}
                  required={assignmentsRequired}
                />
              )}

              {(message.messageType === 'ext' || message.messageType === 'ext-ad')
                && (user.can('message.upload') || user.can('advertisement.request'))
                && mediaFormat === 'audio'
                && (
                  <SidePanelSection
                    title="Upload"
                    required
                  >
                    <Upload
                      onUpdate={onUpdate}
                      media={message.media}
                      required
                    />
                  </SidePanelSection>
                )}

              {(message.messageType === 'ext' || message.messageType === 'ext-ad'
                || message.messageType === 'std')
                && (user.can('message.upload') || user.can('advertisement.request'))
                && mediaFormat === 'visual'
                && (
                  <SidePanelSection
                    title="Media Upload"
                  >
                    <MediaItems
                      visualFiles={message.combinedVisualFiles}
                      onUpdateVisualFiles={this.onUpdateVisualFiles}
                      onUpdate={onUpdate}
                      resolutions={message.resolutions || []}
                      messageStatus={message.status}
                      resolutionErrors={resolutionErrors}
                    />
                  </SidePanelSection>
                )}

              {isScriptVisible && (
                <SidePanelSection title="Script">
                  <Script
                    script={message.script}
                    type={message.messageType}
                    readType={message.readType}
                    instructions={message.instructions}
                    scriptVisible={isScriptVisible}
                    disableScript={disableInput
                      || (!isNew && type === 'library')
                      || (!isNew && !isContentCurator && !isCopy)}
                    disableInstructions={disableInput
                      || (!isNew && type === 'library')
                      || (!isNew && !isContentCurator && !isCopy)}
                    hasFile={!isUndefined(message.file)}
                    onUpdate={onUpdate}
                  />
                </SidePanelSection>
              )}

              {!isVoiceTalent && (
                <>
                  {this.getShowPublishingOptions() && (
                    <SidePanelSection title="Publishing Options">
                      <PublishingOptions
                        type={message.messageType}
                        startDate={message.startDate}
                        endDate={message.endDate}
                        dueDate={message.dueDate}
                        daysOfWeek={message.daysOfWeek}
                        disableInput={disableInput}
                        isAd={isAd}
                        allowDueDate={message.messageType === 'ad'}
                        isNew={isNew}
                        isRequest={type === 'request'}
                        onUpdate={onUpdate}
                      />
                    </SidePanelSection>
                  )}

                  {(
                    (type === 'library' && message.publishMode !== 'automatic')
                      || type === 'request'
                      || isNew
                  ) && (
                    <SidePanelSection title="Approver Options">
                      <ApproverOptions
                        type={type}
                        approverType={approverType}
                        message={message}
                        disableInput={disableInput}
                        required={approversRequired}
                        isNew={isNew}
                        isCopy={isCopy}
                        isReRequest={isReRequest}
                        // allowEditApprover={isContentCurator || isNew || isCopy}
                        allowEditApprover={!disableInput
                          && message.status !== 'final-cut-uploaded'
                          && (type === 'request' || (type === 'library' && isNew))}
                        onUpdate={onUpdate}
                        onClickApprover={this.onClickApprover}
                      />
                    </SidePanelSection>
                  )}
                </>
              )}

              {isAd && (
                <SidePanelSection title="Tracking">
                  <Tracking
                    trackingUrls={message.trackingUrls}
                    onUpdate={onUpdate}
                  />
                </SidePanelSection>
              )}
            </div>
          )}
        </SidePanelContent>

        <SidePanelFooter className="panel-footer">
          {!isVoiceTalent && (
            <VibeButton
              className="btn-save"
              text={this.getSaveMessage()}
              color="secondary"
              loadingEvent="onSaveMessage"
              disabled={disableSave || disableInput}
              onClick={this.onSave}
            />
          )}

          {!isNew ? (
            <div className="toolbar-buttons">
              {showReRequest && (
                <VibeButton
                  variant="outlined"
                  className="toolbar-button"
                  text="Re-request"
                  color="secondary"
                  link={NavigationHelper.updateParams({
                    type: 're-request',
                  })}
                  icon={(
                    <VibeIcon
                      icon={viRedo}
                      color={color.primary}
                      size={16}
                    />
                  )}
                  onClick={this.onReRequest}
                />
              )}

              {showRequestChanges && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viChangeRequest}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.orange,
                    }}
                    tooltip="Request Changes"
                    color={color.orange}
                    size={20}
                    onClick={this.onClickRequestChanges}
                  />
                </div>
              )}

              {showDeny && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viCloseCircle}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.error,
                    }}
                    tooltip="Deny Message"
                    color={color.error}
                    size={20}
                    onClick={this.onClickRequestChanges}
                  />
                </div>
              )}

              {showApproveActions && !copying && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viCheckCircle}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.success,
                    }}
                    tooltip="Approve Message"
                    color={color.success}
                    size={20}
                    onClick={this.onClickApprove}
                  />
                </div>
              )}

              {showApproveOverrideAction && (
                <div className="toolbar-button">
                  <VibeButton
                    variant="outlined"
                    text="Override"
                    color="secondary"
                    tooltip="Globally Approve Message"
                    onClick={this.onClickApproveOverride}
                  />
                </div>
              )}

              {showEmailAction && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viEmail}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.manatee,
                    }}
                    tooltip="Email Approver"
                    color={color.manatee}
                    size={20}
                    onClick={this.onClickEmailApprover}
                  />
                </div>
              )}

              {isContentCurator
                && type === 'request'
                && !message.isExternal
                && message.status !== 'completed'
                && !copying
                && message.mediaFormat === 'audio'
                && (
                  <div className="toolbar-button">
                    <VibeIcon
                      icon={viMusicVoice}
                      type="button"
                      buttonProps={{
                        size: 32,
                        borderColor: color.manatee,
                      }}
                      tooltip={message.voiceTalentId
                        ? 'Re-assign DJ'
                        : 'Assign DJ'}
                      color={color.manatee}
                      size={20}
                      onClick={this.onClickAssignVoiceTalent}
                    />
                  </div>
                )}

              {(showDownload && mediaFormat === 'audio') && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viDownload}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.manatee,
                    }}
                    tooltip="Download"
                    color={color.manatee}
                    size={20}
                    onClick={this.onClickDownload}
                  />
                </div>
              )}

              {showCopy && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viCopy}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.primary,
                    }}
                    color={color.primary}
                    size={20}
                    tooltip="Duplicate"
                    link={NavigationHelper.updateParams({
                      type: 'copy',
                    })}
                  />
                </div>
              )}

              {(showUpload && mediaFormat === 'audio' && !copying) && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viUpload}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.manatee,
                    }}
                    tooltip={uploadTooltip}
                    color={color.manatee}
                    size={20}
                    disabled={disableUpload}
                    onClick={this.onClickUpload}
                  />
                </div>
              )}

              {showArchive && !isArchived && !copying && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viArchive}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.error,
                    }}
                    tooltip="Archive"
                    color={color.error}
                    size={20}
                    onClick={this.onClickArchive}
                  />
                </div>
              )}

              {showArchive && isArchived && (
                <div className="toolbar-button">
                  <VibeIcon
                    icon={viUnarchive}
                    type="button"
                    buttonProps={{
                      size: 32,
                      borderColor: color.success,
                    }}
                    tooltip={locationCount.total > 0
                      ? 'Unarchive'
                      : 'Must have at least 1 active location to unarchive'}
                    color={color.success}
                    size={20}
                    disabled={locationCount.total <= 0}
                    onClick={this.onClickUnarchive}
                  />
                </div>
              )}
            </div>
          ) : null}
        </SidePanelFooter>

        {requestChanges && (
          <RequestChanges
            approverType={approverType}
            messageId={message._id}
            messageType={message.messageType}
            onClose={this.onCloseRequestChanges}
            onRefresh={onRefresh}
          />
        )}

        {assignVoiceTalent && (
          <AssignVoiceTalent
            messageId={message._id}
            voiceTalentId={message.voiceTalentId}
            voiceTalentName={`${message.voiceTalentFname} ${message.voiceTalentLname}`}
            onClose={this.onCloseAssignVoiceTalent}
          />
        )}

        {assignApprover && (
          <ApproverList
            data={message}
            approverType={approverType}
            objectType="message"
            onUpdate={onUpdate}
            onClose={this.onCloseApprovers}
          />
        )}

        {showEmailApprover ? (
          <AssignApprover
            companyId={message.companyId}
            partnerId={message.partnerId}
            locations={message.locations}
            approverType={approverType}
            onChoose={this.onChooseApproverToEmail}
            onClose={this.onCloseApproverEmail}
          />
        ) : null}

        {downloadCut ? (
          <DownloadCut
            roughCutUrl={message.roughCutUrl}
            finalCutUrl={message.finalCutUrl}
            onClose={this.onCloseDownload}
          />
        ) : null}

        {uploadCut ? (
          <UploadCut
            messageId={message._id}
            messageType={message.messageType}
            voiceTalentId={message.voiceTalentId}
            onClose={this.onCloseUpload}
          />
        ) : null}

        <VibeModal
          show={savedAdditionalVisualFiles && message.status === 'final-cut-uploaded'}
          type="success"
          title="Ready For Approval"
          text="This message request is ready for approval and will now appear in the Awaiting Approval list."
          onClose={this.onCloseApprovalModal}
        />

        <VibeModal
          show={confirmArchive}
          type="confirm"
          title="Archive Message"
          text={`Are you sure you want to archive ${message.name}?`}
          confirmProps={{
            color: 'error',
          }}
          onConfirm={this.onConfirmArchive}
          onClose={this.onCloseArchive}
        />

        <VibeModal
          show={showConfirmNoMB}
          type="confirm"
          title="Assign Message Blocks"
          text="This request has not been assigned to any message blocks. Proceed?"
          onConfirm={this.onConfirmAndSend}
          onClose={this.onCancelAndMsgBlocks}
        />

        <VibeModal
          show={confirmApprove}
          type="confirm"
          title="Approve Message"
          text="Would you like to approve this message?"
          onConfirm={this.onConfirmApprove}
          onClose={this.onCloseApprove}
        />

        <VibeModal
          show={confirmApproveOverride}
          type="confirm"
          title="Approve Message - Override"
          text="Override the approval process and immediately approve this message?"
          onConfirm={this.onConfirmApproveOverride}
          onClose={this.onCloseApproveOverride}
        />

        <VibeModal
          show={showInvalidMessageBlocks}
          type="confirm"
          maxWidth="md"
          fullWidth
          title="Current location selection no longer allows for these message blocks"
          text={(
            <div
              style={{
                display: 'flex',
                justifyContent:
                  invalidMessageBlocks.length <= 2
                    ? 'center'
                    : 'left',
                overflow: 'auto',
              }}
            >
              {invalidMessageBlocks.map((block) => {
                return (
                  <MessageBlockCard
                    key={block._id}
                    message={block}
                    height={250}
                    hideTags
                  />
                );
              })}
            </div>
          )}
          confirmProps={{
            color: 'error',
          }}
          onConfirm={this.onConfirmRemoveInvalidMessageBlocks}
          onClose={this.onCancelRemoveInvalidMessageBlocks}
        />
      </SidePanelContainer>
    );
  }
}

MessageDetails.propTypes = {
  /** Type of Message */
  type: PropTypes.string.isRequired,
  /** New Message */
  isNew: PropTypes.bool,
  isCopy: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  message: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  onClose: PropTypes.func,
  onToggleChat: PropTypes.func,
  onToggleHistory: PropTypes.func,
  onUpdate: PropTypes.func,
  /** Refresh the message from the API */
  onRefresh: PropTypes.func,
};

MessageDetails.defaultProps = {
  isNew: false,
  isCopy: false,
  isDuplicate: false,
  message: {},
  onClose: () => {},
  onToggleChat: () => {},
  onToggleHistory: () => {},
  onUpdate: () => {},
  onRefresh: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MessageDetails));
